import { DeviceDriver, RawData } from "@/lib/drivers/DeviceDriver";
import { ACKPayload, COMMAND_ACK_RESPONSE } from "@/lib/drivers/tandem/commands/ACK";
import {
  emptyEventList,
  eventErrorSchema,
  EventList,
  TandemEvent,
  TandemEventError,
} from "@/lib/drivers/tandem/commands/Event";
import { PersonalProfile } from "@/lib/drivers/tandem/commands/PersonalProfile";
import { TandemVersionInfo } from "@/lib/drivers/tandem/commands/Version";
import { TandemCommands, TandemResponses } from "@/lib/drivers/tandem/TandemCommands";
import { TandemConstants } from "@/lib/drivers/tandem/TandemConstants";
import {
  TandemCommand,
  TandemData,
  TandemImportRawInfo,
  TandemPacket,
  tandemUploadInfoSchema,
} from "@/lib/drivers/tandem/TandemTypes";
import { TandemBasalRateEventTransformer } from "@/lib/drivers/tandem/transformers/TandemBasalRateEventTransformer";
import { TandemBolusInsulinTransformer } from "@/lib/drivers/tandem/transformers/TandemBolusInsulinTransformer";
import { TandemDeviceTransformer } from "@/lib/drivers/tandem/transformers/TandemDeviceTransformer";
import { TandemGlycemiaTransformer } from "@/lib/drivers/tandem/transformers/TandemGlycemiaTransformer";
import { TandemPumpEventsTransformer } from "@/lib/drivers/tandem/transformers/TandemPumpEventsTransformer";
import { BufferRef, SerialDevice } from "@/lib/serial/SerialDevice";
import structFn from "@/lib/struct";
import { DeviceDataToUpload } from "@/models/DeviceDataToUpload";
import { MedicalDevice } from "@/models/DeviceModel";
import { Sentry } from "@/utils/sentryUtils";

const struct = structFn();

type Batch = {
  start: number; // inclusive
  end: number; // exclusive
  length: number; // number of records
};

export class TandemDriver extends DeviceDriver<TandemVersionInfo, TandemData> {
  readonly name = "Tandem Driver";
  private readonly device: SerialDevice<TandemPacket<unknown>>;
  private sendIsPending = false;
  private packets: TandemPacket<unknown>[] = [];
  private importStart: number = 0;

  constructor(
    connection: SerialPort,
    private readonly tandemDevices: MedicalDevice[],
  ) {
    super();
    this.device = new SerialDevice<TandemPacket<unknown>>(connection, {
      vendorId: TandemConstants.VENDOR_ID,
      productId: TandemConstants.PRODUCT_ID,
      bitrate: TandemConstants.BITRATE,
      packetHandler: this._tandemPacketHandler,
      retries: TandemConstants.MAX_TRIES,
    });
  }

  private _listenPacketAbortTimer: unknown;
  set listenPacketAbortTimer(value: unknown) {
    clearTimeout(this._listenPacketAbortTimer as number);
    this._listenPacketAbortTimer = value;
  }

  private _listenPacketRetryTimer: unknown;
  set listenPacketRetryTimer(value: unknown) {
    clearTimeout(this._listenPacketRetryTimer as number);
    this._listenPacketRetryTimer = value;
  }

  private _listenPacketInterval: unknown;
  set listenPacketInterval(value: unknown) {
    clearInterval(this._listenPacketInterval as number);
    this._listenPacketInterval = value;
  }

  private _batchPacketTimeout: unknown;
  set batchPacketTimeout(value: unknown) {
    clearTimeout(this._batchPacketTimeout as number);
    this._batchPacketTimeout = value;
  }

  private _batchPacketInterval: unknown;
  set batchPacketInterval(value: unknown) {
    clearInterval(this._batchPacketInterval as number);
    this._batchPacketInterval = value;
  }

  private _waitForReconnectInterval: unknown;
  set waitForReconnectInterval(value: unknown) {
    clearInterval(this._waitForReconnectInterval as number);
    this._waitForReconnectInterval = value;
  }

  private _waitForReconnectTimeout: unknown;
  set waitForReconnectTimeout(value: unknown) {
    clearTimeout(this._waitForReconnectTimeout as number);
    this._waitForReconnectTimeout = value;
  }

  private _fetchRecordsSender: unknown;
  set fetchRecordsSender(value: unknown) {
    clearInterval(this._fetchRecordsSender as number);
    this._fetchRecordsSender = value;
  }

  private _fetchRecordsInterval: unknown;
  set fetchRecordsInterval(value: unknown) {
    clearInterval(this._fetchRecordsInterval as number);
    this._fetchRecordsInterval = value;
  }

  _weakChecksum = (bytes: Uint8Array, offset: number, count: number) => {
    let total = 0;
    for (let i = 0; i < count; ++i) {
      total += bytes[i + offset];
    }
    return total & 0xffff;
  };

  _buildPacket = <T, U = T>(
    descriptor: TandemCommand<T, U>,
    payloadLength: number,
    payload?: Uint8Array,
  ): Uint8Array => {
    const buf = new ArrayBuffer(payloadLength + 9);
    const bytes = new Uint8Array(buf);
    let ctr = struct.pack(bytes, 0, "bbb", TandemConstants.SYNC_BYTE, descriptor.value, payloadLength);
    ctr += struct.copyBytes(bytes, ctr, payload, payloadLength);
    const checksum = this._weakChecksum(bytes, 1, ctr - 1);
    struct.pack(bytes, ctr, "IS", 0, checksum); // the checksum is big-endian and timestamp always 0
    return bytes;
  };
  // accepts a stream of bytes and tries to find a Tandem packet
  // at the beginning of it. in no case should there be fewer than 9 bytes
  // in the byte-stream.
  // returns a packet object; if valid === true it's a valid packet
  // if packet_len is nonzero, that much should be deleted from the stream
  // if valid is false and packet_len is nonzero, the previous packet
  // should be NAKed.
  _extractPacket = (bytes: Uint8Array): TandemPacket<unknown> => {
    const packet: TandemPacket<Uint8Array | undefined> = {
      valid: false,
      sync: 0,
      descriptor: 0,
      payload_len: 0,
      payload: undefined,
      crc: 0,
      packet_len: 0,
      body: undefined,
    };

    const payloadLen = bytes.length;
    if (payloadLen < 9) {
      return packet;
    }

    // we know we have at least enough to check the packet header, so do that
    struct.unpack(bytes, 0, "bbb", ["sync", "descriptor", "payload_len"], packet);
    // if the first byte isn't our sync byte, then just discard that
    // one byte and let our caller try again.
    if (packet.sync != TandemConstants.SYNC_BYTE) {
      packet.packet_len = 1;
      return packet;
    }

    const need_len = packet.payload_len + 9;
    if (need_len > payloadLen) {
      return packet; // we don't have enough yet so go back for more
    }
    packet.packet_len = need_len;

    // we now have enough length for a complete packet, so calc the CRC
    packet.crc = struct.extractBEShort(bytes, packet.packet_len - 2);
    const checksum = this._weakChecksum(bytes, 1, packet.packet_len - 3);
    if (checksum != packet.crc) {
      // if the crc is bad, we should discard the whole packet
      // (packet_len is nonzero)
      console.warn(`Bad Checksum for packet, ${packet.crc} != ${checksum}`);
      return packet;
    }

    if (packet.payload_len) {
      /* TODO handle IDP_TE timestamp
      if (packet.descriptor === TandemConstants.RESPONSES.IDP_TE.value) {
        // Tandem only returns timestamps for when an event occurred for
        // the event history log entries. For other records, e.g. personal profile (IDP_TE)
        // or reminder settings, the only available timestamp is the packet timestamp,
        // i.e., the device time at time of upload.
        packet.timestamp = struct.extractBEInt(bytes, packet.packet_len - 6);
      }*/

      const payload = new Uint8Array(packet.payload_len);
      for (let i = 0; i < packet.payload_len; ++i) {
        payload[i] = bytes[i + 3];
      }
      packet.payload = payload;
      const descriptor = packet.descriptor;
      const response =
        descriptor in TandemResponses ? TandemResponses[descriptor as keyof typeof TandemResponses] : undefined;
      if (response && response.fields && response.format && response.schema) {
        const unpackedPayload = struct.unpack(bytes, 3, response.format, response.fields);
        const parsedPayload = response.schema.safeParse(unpackedPayload);
        if (!parsedPayload.success) {
          console.warn("Failed to parse payload", parsedPayload.error);
          return packet;
        }
        let finalPayload = parsedPayload.data;
        if (response.postprocess && parsedPayload.success) {
          // @ts-expect-error - we know that the parsedPayload is valid for the postprocess function
          finalPayload = response.postprocess(finalPayload);
        }
        return {
          ...packet,
          valid: true,
          payload: finalPayload,
        };
      }
      packet.valid = true;
      return packet;
    } else {
      packet.valid = true;
      return packet;
    }
  };

  _tandemPacketHandler = (buffer: BufferRef) => {
    // first, discard bytes that can't start a packet
    while (buffer.len() > 0 && buffer.get(0) !== TandemConstants.SYNC_BYTE) {
      buffer.discard(1);
    }

    if (buffer.len() < 9) {
      // all complete packets must be at least this long
      return null; // not enough there yet
    }

    // there's enough there to try, anyway
    const packet = this._extractPacket(buffer.bytes());
    if (packet.packet_len !== 0) {
      // remove the now-processed packet
      buffer.discard(packet.packet_len);
    }
    if (packet.valid) {
      this.packets.push(packet);
      return packet;
    } else {
      return null;
    }
  };

  _tandemCommand = async <T, U = T>(command: TandemCommand<T, U>, args?: string[]) => {
    const format = command.format;
    let payload;
    let payload_len = 0;
    if (format) {
      payload_len = struct.structlen(format);
      payload = new Uint8Array(payload_len);
      if (args) {
        switch (args.length) {
          case 1:
            struct.pack(payload, 0, format, args);
            break;
          case 2:
            struct.pack(payload, 0, format, args[0], args[1]);
            break;
          default:
            throw new Error("Unsupported number of arguments");
        }
      }
    }

    const commandPacket = this._buildPacket(command, payload_len, payload);

    try {
      await this.device.writeSerial(commandPacket);
    } catch (e) {
      Sentry.captureException(e);
      if (
        e &&
        typeof e === "object" &&
        "message" in e &&
        typeof e.message === "string" &&
        e.message.includes("WritableStream is locked")
      ) {
        this.sendIsPending = true;
        setTimeout(() => {
          this.sendIsPending = false;
        }, TandemConstants.SEND_WAIT);
      }
    }
  };

  _listenForPacket = <T, U = T>(
    command: TandemCommand<T, U>,
    args?: string[],
  ): Promise<TandemPacket<U | undefined>> => {
    return new Promise((resolve, reject) => {
      this.listenPacketAbortTimer = setTimeout(() => {
        this.listenPacketRetryTimer = undefined;
        this.listenPacketAbortTimer = undefined;
        this.listenPacketInterval = undefined;
        console.info(`Tandem packet timeout, for command ${command.name}`);
        reject(new Error("Timeout error"));
      }, TandemConstants.RETRY_TIMEOUT * 2);

      this.listenPacketRetryTimer = setTimeout(async () => {
        console.info(`Retrying tandem command ${command.name} with args ${args}`);
        await this._tandemCommand(command, args);
      }, TandemConstants.RETRY_TIMEOUT);

      this.listenPacketInterval = setInterval(() => {
        if (this.device.hasAvailablePacket()) {
          const pkt = this.device.nextPacket();
          if (pkt && pkt.valid && "response" in command && command.response.value === pkt.descriptor) {
            this.listenPacketRetryTimer = undefined;
            this.listenPacketAbortTimer = undefined;
            this.listenPacketInterval = undefined;
            // The packet handler already parsed and verified the payload, so we can just cast it
            resolve(pkt as TandemPacket<U>);
          } else if (pkt && pkt.valid && pkt.descriptor === COMMAND_ACK_RESPONSE.value) {
            this.listenPacketRetryTimer = undefined;
            this.listenPacketAbortTimer = undefined;
            this.listenPacketInterval = undefined;
            console.info(`ACK received for command ${command.name}`);
            resolve({
              ...pkt,
              payload: undefined,
            });
          } else {
            // TODO handle unexpected ACK response differently ?
            console.info("Packet not valid for command", command.name);
          }
        }
      }, 1); // spin on this one quickly
    });
  };

  _tandemCommandResponse = async <T, U = T>(command: TandemCommand<T, U>, args?: string[]) => {
    await this._tandemCommand(command, args);
    return await this._listenForPacket(command);
  };

  _deviceInfo = async (): Promise<TandemVersionInfo | undefined> => {
    const result = await this._tandemCommandResponse(TandemCommands.VERSION_COMMAND);
    return result.payload;
  };

  _isMultiRecordFirmware = (firmwareVersion: number) => {
    return firmwareVersion >= TandemCommands.MULTI_EVENT_COMMAND.version;
  };

  _getBatches = (startSeq: number, endSeq: number, batchSize: number): Batch[] => {
    // start from the end and go backwards
    // Return a list of all batches to fetch
    const batches = [];
    let currentSeq = endSeq;
    while (currentSeq > startSeq) {
      const end = currentSeq;
      const start = Math.max(currentSeq - batchSize, startSeq);
      batches.push({ start, end, length: end - start });
      currentSeq = start;
    }
    return batches;
  };

  _fetchBatch = (batch: Batch) => {
    console.info(`Fetching Tandem batch from ${batch.start} to ${batch.end}`);
    return this._tandemCommand(TandemCommands.MULTI_EVENT_COMMAND, [
      batch.start.toString(),
      (batch.length + 1).toString(),
    ]);
  };

  /**
   * Listen for packets in a batch after a batch request
   * Timeout after 1 second
   *
   * @param batch
   */
  _listenForBatchPackets = async (batch: Batch): Promise<EventList | "TIMEOUT"> => {
    return new Promise(_resolve => {
      const resolve = (result: EventList | "TIMEOUT") => {
        this.batchPacketInterval = undefined;
        this.batchPacketTimeout = undefined;
        _resolve(result);
      };
      const result = emptyEventList(batch.length);
      this.batchPacketTimeout = setTimeout(() => {
        resolve("TIMEOUT");
      }, 1000);
      this.batchPacketInterval = setInterval(() => {
        while (this.device.hasAvailablePacket()) {
          const packet = this.device.nextPacket();
          if (packet) {
            // We have a packet
            if (packet.valid && this._packetIsEvent(packet)) {
              const event = packet.payload;
              if (event.valid) {
                result.events.push(event);
                result.receivedCount++;
              } else {
                result.ignoredCount++;
              }
            } else {
              result.missedCount++;
            }
            result.totalCount++;
          }
        }
        if (result.totalCount >= batch.length) {
          resolve(result);
        }
      }, TandemConstants.INTERVAL_FREQ);
    });
  };

  _fetchMultiRecords = async (startSeq: number, endSeq: number): Promise<EventList> => {
    console.info(`Fetching Tandem multi records from ${startSeq} to ${endSeq}`);
    const batches = this._getBatches(startSeq, endSeq, TandemConstants.MAX_RECORDS_PER_REQUEST);

    let currentBatchIndex = 0;

    const result = emptyEventList(endSeq - startSeq + 1);
    let retryCount = 0;

    const waitForReconnect = () =>
      new Promise(resolve => {
        this.pauseProgress("devices.pause.usbConnectionLost");
        this.waitForReconnectTimeout = setTimeout(() => {
          this.waitForReconnectInterval = undefined;
          this.waitForReconnectTimeout = undefined;
          resolve(undefined);
        }, 60_000);
        this.waitForReconnectInterval = setInterval(() => {
          if (this.device.deviceStatus === "connected") {
            this.resumeProgress();
            this.waitForReconnectInterval = undefined;
            this.waitForReconnectTimeout = undefined;
            resolve(undefined);
          }
        }, TandemConstants.INTERVAL_FREQ);
      });

    while (currentBatchIndex < batches.length) {
      // We wait for a short time before fetching the next batch
      await new Promise(resolve => setTimeout(resolve, TandemConstants.INTERVAL_FREQ));
      const batch = batches[currentBatchIndex];
      this.device.flush();
      await this._fetchBatch(batch);
      const batchResult = await this._listenForBatchPackets(batch);

      if (batchResult === "TIMEOUT") {
        if (this.device.deviceStatus !== "connected") {
          await waitForReconnect();
        }
        if (retryCount > 2) {
          currentBatchIndex++;
          retryCount = 0;
        } else {
          retryCount += 1;
        }
      } else {
        if (batchResult.issue === "NONE") {
          result.events.push(...batchResult.events);
          result.receivedCount += batchResult.receivedCount;
          result.ignoredCount += batchResult.ignoredCount;
          result.missedCount += batchResult.missedCount;
          result.totalCount += batchResult.totalCount;
          currentBatchIndex++;
        } else {
          currentBatchIndex++;
        }
        retryCount = 0;
      }

      this.setProgress(result.totalCount);
    }

    return result;
  };

  _packetIsEvent = (packet: TandemPacket<unknown>): packet is TandemPacket<TandemEvent<unknown>> => {
    return packet.descriptor === TandemCommands.EVENT_COMMAND.response.value;
  };

  _packetIsAck = (packet: TandemPacket<unknown>): packet is TandemPacket<ACKPayload> => {
    return packet.descriptor === COMMAND_ACK_RESPONSE.value;
  };

  _fetchRecords = async (startSeq: number, endSeq: number): Promise<EventList> => {
    console.info(`Fetching Tandem records from ${startSeq} to ${endSeq}`);

    return new Promise((resolve, reject) => {
      let sendSeq = startSeq;
      let currentSeq = startSeq;
      let tries = 0;
      const result = emptyEventList(endSeq - startSeq + 1);

      this.fetchRecordsInterval = setInterval(async () => {
        if (currentSeq > endSeq) {
          // All asked records received
          this.fetchRecordsInterval = undefined;
          this.fetchRecordsSender = undefined;
          this.device.flush();
          resolve(result);
        }
        while (this.device.hasAvailablePacket() && !this.sendIsPending) {
          // For each packet
          const packet = this.device.nextPacket();
          if (packet && packet.valid) {
            try {
              if (!this._packetIsAck(packet)) {
                // If the packet is not an ACK, reset the retry counter
                tries = 0;
              }
              switch (true) {
                case this._packetIsEvent(packet) && currentSeq < packet.payload.eventSequenceNum: // A record was missed, the packet is ahead of the current sequence
                  // Set back the sender to current
                  sendSeq = currentSeq;
                  break;
                case this._packetIsAck(packet): // An ACK was received, this is an error
                  // Set back the sender to current and count retries. If max retries is reached, skip the current sequence
                  sendSeq = currentSeq;
                  tries++;
                  if (tries > TandemConstants.MAX_TRIES) {
                    currentSeq++;
                    result.missedCount++;
                    result.totalCount++;
                    tries = 0;
                  }
                  break;
                case this._packetIsEvent(packet) && currentSeq === packet.payload.eventSequenceNum: // The packet is the expected one
                  this.setProgress(currentSeq - startSeq);
                  if (packet.payload.valid) {
                    result.events.push(packet.payload);
                    result.receivedCount++;
                  } else {
                    result.ignoredCount++;
                  }
                  result.totalCount++;
                  currentSeq++;
                  break;
              }
            } catch (e) {
              Sentry.captureException(e);
              this.fetchRecordsInterval = undefined;
              this.fetchRecordsSender = undefined;
              reject(new TandemEventError("UNKNOWN"));
            }
          }
        }
      }, TandemConstants.INTERVAL_FREQ);

      this.fetchRecordsSender = setInterval(async () => {
        if (!this.sendIsPending) {
          try {
            await this._tandemCommand(TandemCommands.EVENT_COMMAND, [sendSeq.toString()]);
            if (sendSeq < endSeq) {
              sendSeq++;
            }
          } catch (e) {
            Sentry.captureException(e);
            this.fetchRecordsInterval = undefined;
            this.fetchRecordsSender = undefined;
            reject(new TandemEventError("UNKNOWN"));
          }

          sendSeq++;
        }
      }, TandemConstants.INTERVAL_FREQ);
    });
  };

  _fetchEvents = async (
    firmwareVersion: number,
    startSeq: number,
    endSeq: number,
    mode: "NO_MULTI" | "NORMAL" = "NORMAL",
  ): Promise<EventList> => {
    console.info("Fetching Tandem events");

    const supportsMultiRecord = this._isMultiRecordFirmware(firmwareVersion);

    try {
      return supportsMultiRecord && mode === "NORMAL"
        ? await this._fetchMultiRecords(startSeq, endSeq)
        : await this._fetchRecords(startSeq, endSeq);
    } catch (e) {
      Sentry.captureException(e);
      const error = eventErrorSchema.safeParse(e);
      if (error.success) {
        if (error.data.type === "RETRY") {
          return this._fetchEvents(firmwareVersion, startSeq, endSeq, "NORMAL");
        }

        return emptyEventList(endSeq - startSeq + 1, error.data.type);
      }
      return emptyEventList(endSeq - startSeq + 1, "UNKNOWN");
    }
  };

  _fetchEventRange = async (versionInfo: TandemVersionInfo) => {
    console.info("Fetching Tandem event range");

    // Set event range to last_fetched_sequence or 0
    let startSeq = 0;
    const device = this.tandemDevices.find(device => device.serialNumber === versionInfo.pumpSerial.toString());
    if (device) {
      const tandemUploadInfo = tandemUploadInfoSchema.safeParse(device.lastUploadInfo);

      if (tandemUploadInfo.success) {
        startSeq = tandemUploadInfo.data.last_fetched_sequence;
      }
    }

    // Get number of events
    const logsSize = await this._tandemCommandResponse(TandemCommands.LOG_SIZE_COMMAND);
    this.device.flush(); // making sure we flush the buffers

    const payload = logsSize.payload;
    if (!payload) {
      throw new Error("Failed to get log size");
    }
    const endSeq = payload.endSeq;
    if (endSeq < startSeq) {
      throw new Error("Invalid log size");
    }
    startSeq = Math.max(startSeq, endSeq - TandemConstants.MAX_TOTAL_RECORDS);

    this.setProgressWhenDone(endSeq - startSeq);

    const events = await this._fetchEvents(versionInfo.firmwareVersion, startSeq, endSeq);
    return {
      ...events,
      startSeq,
      endSeq,
    };
  };

  _fetchProfiles = async () => {
    console.info("Fetching Tandem profiles");
    const profilesList = await this._tandemCommandResponse(TandemCommands.PERSONAL_PROFILE_LIST_COMMAND);

    const payload = profilesList.payload;

    if (!payload) {
      return [];
    }

    const result: PersonalProfile[] = [];

    for (const i of payload.slots) {
      const profile = await this._tandemCommandResponse(TandemCommands.PERSONAL_PROFILE_COMMAND, [i.toString()]);
      if (profile.payload) {
        result.push(profile.payload);
      }
    }

    return result;
  };

  _fetchSettings = async () => {
    console.info("Fetching Tandem global settings, max bolus and personal profiles");
    const globals = await this._tandemCommandResponse(TandemCommands.GLOBAL_DATA_COMMAND);
    const maxBolus = await this._tandemCommandResponse(TandemCommands.MAX_BOLUS_COMMAND);
    const controlIq = await this._tandemCommandResponse(TandemCommands.CONTROL_IQ_COMMAND);
    const profilesList = await this._fetchProfiles();

    if (!globals.payload || !maxBolus.payload) {
      throw new Error("Failed to get global settings or profiles");
    }

    return {
      globals: globals.payload,
      maxBolus: maxBolus.payload,
      profiles: profilesList,
      controlIq: controlIq.payload,
    };
  };

  connect = async () => {
    console.info("Connecting to Tandem");

    try {
      this.initProgress();
      this.setProgress(0);
      this.packets = [];
      this.importStart = Date.now();
      return await this.device.connect();
    } catch (e) {
      Sentry.captureException(e);
      return false;
    }
  };

  retrieveInfo = async (): Promise<TandemVersionInfo> => {
    console.info("Getting Tandem config info");

    const result = await this._deviceInfo();
    if (!result) {
      throw new Error("Failed to get device info");
    }
    return result;
  };

  retrieveEvents = async (versionInfo: TandemVersionInfo) => {
    console.info("Fetching Tandem data");

    const events = await this._fetchEventRange(versionInfo);
    const settings = await this._fetchSettings();

    return {
      settings,
      events,
      versions: versionInfo,
    };
  };

  buildImportRawInfo = (config: TandemVersionInfo, data: TandemData): RawData => {
    console.info("Building import raw info from Tandem data");

    const raw: TandemImportRawInfo = {
      import_start_date: new Date(this.importStart).toISOString(),
      import_end_date: new Date().toISOString(),
      version_info: config,
      data,
      raw_packets: this.packets,
    };
    const devices = new TandemDeviceTransformer().transform(data);
    return {
      device: devices.pumpDevice,
      raw: JSON.stringify(raw),
    };
  };

  disconnect = async () => {
    console.info("Disconnecting Tandem");

    try {
      this.listenPacketRetryTimer = undefined;
      this.listenPacketAbortTimer = undefined;
      this.listenPacketInterval = undefined;
      this.batchPacketTimeout = undefined;
      this.batchPacketInterval = undefined;
      this.waitForReconnectInterval = undefined;
      this.waitForReconnectTimeout = undefined;
      this.fetchRecordsInterval = undefined;
      this.fetchRecordsSender = undefined;

      this.initProgress();
      this.packets = [];
      this.importStart = 0;
      await this.device.disconnect();
      return true;
    } catch (e) {
      Sentry.captureException(e);
      return false;
    }
  };

  transformForUpload = (data: TandemData): DeviceDataToUpload => {
    console.info("Transforming Tandem data for upload");

    const devices = new TandemDeviceTransformer().transform(data);
    const bolus = new TandemBolusInsulinTransformer().transform(data);
    const pumpEvents = new TandemPumpEventsTransformer().transform(data);
    const basal = new TandemBasalRateEventTransformer().transform(data);
    const glycemia = new TandemGlycemiaTransformer().transform(data);

    return {
      ...devices,
      pumpEvents,
      bolus,
      basal,
      glycemia,
    };
  };
}
